import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

import coverImage from "@/assets/blog/actions-fidelisation-client/couverture.webp";
import pyramideImage from "@/assets/blog/actions-fidelisation-client/pyramide-clients-occasionnels-reguliers-vip.webp";
import parrainageImage from "@/assets/blog/actions-fidelisation-client/parrainage-client-notification-push-processus.webp";
import roadmapImage from "@/assets/blog/actions-fidelisation-client/roadmap-30-jours-programme-fidelisation.webp";

const ActionsFidelisationClient = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Fidélisation client : 7 actions concrètes à mettre en place dès cette semaine",
      seoConfig.blogActionsFidelisation.description,
      "/7-actions-fidelisation-client"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "7 actions de fidélisation", path: "/7-actions-fidelisation-client" }
    ]),
    generateArticleSchema(
      "Fidélisation client : 7 actions concrètes à mettre en place dès cette semaine",
      seoConfig.blogActionsFidelisation.description,
      "https://unotif.com/7-actions-fidelisation-client",
      "2026-04-22"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogActionsFidelisation} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">7 actions de fidélisation</span>
            </nav>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Fidélisation client : 7 actions concrètes à mettre en place dès cette semaine
            </h1>

            <img
              src={coverImage}
              alt="Actions concrètes de fidélisation client faciles à appliquer rapidement pour augmenter la rétention et l’engagement"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Savoir que la <strong className="font-semibold">fidélisation client</strong> est rentable est une chose. Savoir par où commencer en est une autre. La plupart des commerçants et entrepreneurs remettent ce chantier à "plus tard" parce que le sujet leur semble vaste, technique, ou réservé aux structures avec des équipes dédiées.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Aucune de ces raisons n'est fondée. Les 7 <strong className="font-semibold">actions de fidélisation</strong> qui suivent sont réalisables en une semaine, applicables à n'importe quel type de commerce ou d'activité de service, et ne nécessitent ni expertise marketing ni budget conséquent. À la fin de cet article, vous aurez une roadmap claire sur 30 jours. Si vous cherchez d'abord à comprendre les fondamentaux stratégiques, notre guide sur <Link to="/fidelisation-client-guide" className="text-[#0c97a9] hover:underline">comment fidéliser ses clients</Link> pose toute la stratégie avant d'agir.
            </p>

            {/* Action 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 1 : créer un canal de contact direct avec vos clients (Jour 1)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Tout commence là. Sans canal de contact, chaque client qui passe repart dans le vide. Vous ne pouvez pas l'inviter à revenir, pas lui rappeler son solde de points, pas lui envoyer une offre au bon moment. La fidélisation commence au moment où vous avez la capacité de recontacter quelqu'un après sa première visite.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois options sont accessibles sans budget technique. L'email demande un formulaire d'inscription et produit des taux d'ouverture de 23 à 30 %. Le SMS produit des taux d'ouverture supérieurs à 90 % mais a un coût par message. Le <strong className="font-semibold">wallet mobile</strong> se distingue par sa friction quasi nulle : Apple Wallet et Google Wallet sont déjà installés sur tous les smartphones. Le client scanne un QR code posé en caisse, confirme d'un tap, et la carte s'ajoute directement dans son téléphone. Aucun téléchargement, aucun compte à créer. Taux d'adoption 3 à 5 fois supérieur à celui d'une application dédiée.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ce qu'il faut faire aujourd'hui : imprimer un QR code, le poser à côté de la caisse ou l'afficher en vitrine, et tester le parcours vous-même en 5 minutes.
            </p>

            {/* Action 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 2 : mettre en place une récompense atteignable en 3 à 5 visites (Jour 2)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La mécanique de récompense est le cœur visible de tout <strong className="font-semibold">programme fidélité</strong>. Une règle fondamentale avant tout choix : la première récompense doit être atteignable en 3 à 5 visites maximum. Au-delà, le client perd de vue l'objectif et abandonne le programme avant de l'avoir vraiment testé.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Type de commerce</th>
                    <th className="px-4 py-3 text-left font-semibold">Mécanique recommandée</th>
                    <th className="px-4 py-3 text-left font-semibold">Raison</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Café, boulangerie</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Tampon numérique (5 achats = 1 offert)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Achat fréquent, montant faible : la récompense arrive vite</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Salon de coiffure</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Points cumulatifs (1 point par euro)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Visites espacées, panier élevé : la progression est visible</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Boutique</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Offre anniversaire + points cumulatifs</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Achats saisonniers : l'anniversaire crée un retour hors saison</td>
                  </tr>
                  <tr className="bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Coach, formateur</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Parrainage + accès exclusif membres</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">La relation prime sur la récompense matérielle</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le choix entre ces mécaniques repose sur une seule variable : la fréquence naturelle de visite de vos clients. Plus elle est élevée, plus un tampon fonctionne. Plus le panier est élevé et la visite espacée, plus un système de points est adapté.
            </p>

            {/* Action 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 3 : envoyer un premier message de bienvenue personnalisé (Jour 3)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le message de bienvenue est souvent traité comme une formalité. C'est en réalité la première impression de votre relation avec un nouveau membre. Bien rédigé, il installe le ton et crée un premier ancrage émotionnel.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un bon message de bienvenue fait 4 choses en moins de 60 mots : il confirme l'inscription, il nomme le client, il annonce ce qui l'attend (récompense, prochaine communication), et il donne le ton de la relation. Voici un modèle applicable immédiatement : "Bienvenue [prénom]. Votre carte est active. Il vous suffit de 3 visites pour décrocher votre première récompense. On se retrouve bientôt."
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le timing compte. Un message de bienvenue envoyé dans les 24 heures après l'inscription génère un taux de retour 30 % supérieur à un message envoyé 48 heures après. La réactivité signale l'attention.
            </p>

            {/* Action 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 4 : identifier vos 20 clients les plus fidèles et les traiter différemment (Jour 4)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La loi de Pareto s'applique dans presque tous les secteurs : 20 % de vos clients génèrent 50 à 80 % de votre chiffre d'affaires. Ces clients méritent une attention différenciée. Pas besoin d'un CRM pour démarrer : une liste à la main ou un tableur simple suffit.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois critères simples pour les identifier : visite régulière depuis plus de 3 mois, panier moyen supérieur à la moyenne, et client qui vous a déjà recommandé à quelqu'un. Ces critères croisés vous donnent rapidement une liste de 10 à 20 personnes.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Traiter différemment ces clients ne signifie pas forcément leur offrir une remise. Un message personnalisé avant tout le monde, un accès anticipé à une offre, un avantage inattendu sans condition : ces gestes créent plus d'attachement qu'un bon de réduction générique. Ces clients ne cherchent pas de remise. Ils cherchent à être reconnus.
            </p>

            <img
              src={pyramideImage}
              alt="Pyramide de segmentation client avec clients occasionnels réguliers et VIP et leur impact sur le chiffre d’affaires"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Action 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 5 : planifier une communication par mois pour les 3 prochains mois (Jour 5)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              "Je communiquerai quand j'ai quelque chose à dire" est la raison principale pour laquelle la majorité des programmes de fidélisation s'éteignent après 6 semaines. Sans planification, les communications ne se font pas. Et un <strong className="font-semibold">programme de fidélité</strong> sans communication régulière est un programme inactif.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le minimum viable : 1 message par mois, planifié à l'avance sur 90 jours. Trois formats fonctionnent sans nécessiter d'inspiration particulière.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le rappel de progression : "Il vous reste 30 points. Encore 2 visites et votre récompense vous attend." Ce message réactive les membres inactifs depuis 3 semaines et leur donne une raison concrète de revenir.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'offre flash pour les inactifs : ciblée sur les clients absents depuis 30 jours. "Ça fait un moment. Un avantage exclusif vous attend cette semaine." Ce message capte les clients avant qu'ils passent définitivement à autre chose.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'événement exclusif membres : "Vente privée samedi à 18h. Réservé à notre communauté." Ce message crée un sentiment d'appartenance et génère un trafic additionnel prévisible.
            </p>

            {/* Action 6 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 6 : mettre en place un mécanisme de parrainage simple (Semaine 2)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le parrainage est l'action qui transforme la fidélisation en levier d'acquisition. Un <strong className="font-semibold">client ambassadeur</strong> actif génère 2 à 5 nouveaux clients par an, avec un taux de conversion 4 fois supérieur à la publicité standard. Et il le fait sans coût direct.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La règle absolue : si recommander prend plus de 30 secondes, personne ne le fait. Même un client enthousiaste n'ira pas chercher votre site, remplir un formulaire, et transmettre un code à son entourage. Le mécanisme doit être accessible en un tap depuis la carte wallet.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La mécanique minimale : un lien ou QR code de parrainage unique par client, accessible directement depuis sa carte, qui crée une récompense automatique à l'inscription du filleul. Côté parrain : points bonus ou réduction. Côté filleul : offre de bienvenue dédiée. Les deux reçoivent leur avantage automatiquement, sans action manuelle de votre part.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Avant de lancer, testez le parcours complet vous-même. Si vous bloquez quelque part, votre client bloquera aussi.
            </p>

            <img
              src={parrainageImage}
              alt="Processus de parrainage client avec lien envoyé par notification push partage ami inscription et récompense pour les deux"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Action 7 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Action 7 : suivre 3 indicateurs simples chaque semaine (Semaine 2)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme sans mesure est un programme aveugle. Vous ne savez pas si vos actions fonctionnent, vous ne savez pas quoi ajuster, et vous n'avez aucun argument pour investir davantage dedans.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois indicateurs suffisent pour démarrer. Le nombre de nouveaux membres enregistrés par semaine mesure l'efficacité de votre canal d'inscription. La fréquence de visite des membres comparée aux non-membres mesure l'impact réel du programme sur le comportement. Le taux d'ouverture des notifications envoyées mesure la pertinence de vos communications.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Cinq minutes par semaine dans un tableur : notez ces 3 chiffres, calculez la tendance sur 4 semaines, ajustez si un indicateur stagne. Les premiers résultats visibles apparaissent entre 30 et 60 jours après la mise en place des actions 1 à 5.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Avant d'agir, comprendre pourquoi vos clients partent sans se plaindre est une étape utile. Notre guide sur <Link to="/pourquoi-clients-partent" className="text-[#0c97a9] hover:underline">pourquoi 68 % des clients partent en silence</Link> vous aide à identifier ce que vous cherchez à prévenir avec ces 7 actions.
            </p>

            {/* Roadmap */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              La roadmap 30 jours : dans quel ordre enchaîner ces 7 actions
            </h2>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Période</th>
                    <th className="px-4 py-3 text-left font-semibold">Actions</th>
                    <th className="px-4 py-3 text-left font-semibold">Résultat attendu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Semaine 1 (J1 à J5)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Actions 1 à 5 : canal, récompense, bienvenue, VIP, calendrier</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Base posée, premiers membres, premiers contacts</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Semaine 2 (J6 à J10)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Actions 6 et 7 : parrainage et indicateurs</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Acquisition organique amorcée, mesure en place</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Semaine 3 à 4</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Envoyer la première communication planifiée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Premier retour client mesurable</td>
                  </tr>
                  <tr className="bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Mois 2</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Analyser les 3 indicateurs, ajuster un message</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Optimisation basée sur données réelles</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <img
              src={roadmapImage}
              alt="Plan de lancement d’un programme de fidélisation en 30 jours avec étapes fondations activation communication et analyse"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces 7 <strong className="font-semibold">actions de fidélisation client</strong> ne nécessitent ni budget conséquent, ni expertise technique, ni semaines de préparation. Elles nécessitent une décision : commencer. Le seul risque est d'attendre les conditions parfaites et de continuer à voir des clients partir en silence pendant ce temps. Commencez par l'action 1 aujourd'hui : créez un canal de contact direct. Tout le reste se construit à partir de là. Unotif vous permet de réaliser ces 7 actions depuis une seule interface.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ActionsFidelisationClient;
